import React, { JSX } from 'react';
import ServiceRequestHeading from './ServiceRequestHeading/ServiceRequestHeading';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { USER_ROLES } from 'src/common/constants';
import { isRole } from 'src/helpers/permissions';
import ServiceRequestLocationBreadcrumb from './ServiceRequestLocationBreadcrumb/ServiceRequestLocationBreadcrumb';
import Skeleton from '../../../components/Skeleton/Skeleton';
import ServiceRequestReporterCard from 'src/pages/ServiceRequest/components/ServiceRequestReporterCard/ServiceRequestReporterCard';
import ServiceRequestHomeOwnerCard from 'src/pages/ServiceRequest/components/ServiceRequestHomeOwnerCard/ServiceRequestHomeOwnerCard';
import ServiceRequestAssignedToCard from 'src/pages/ServiceRequest/components/ServiceRequestAssignedToCard/ServiceRequestAssignedToCard';
import ServiceRequestAppointmentCard from 'src/pages/ServiceRequest/components/ServiceRequestAppointmentCard/ServiceRequestAppointmentCard';
import ServiceRequestActions from 'src/pages/ServiceRequest/components/ServiceRequestActions/ServiceRequestActions';
import ServiceRequestBreadcrumb from 'src/pages/ServiceRequest/components/ServiceRequestBreadcrumb/ServiceRequestBreadcrumb';
import ServiceRequestStatusToast from 'src/pages/ServiceRequest/components/ServiceRequestStatusToast/ServiceRequestStatusToast';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import SubscribersCard from 'src/pages/ServiceRequest/Subscribers/SubscribersCard';
import ServiceRequestSchedulingNote from 'src/pages/Calendar/CalcomIntegration/components/ServiceRequestSchedulingNote/ServiceRequestSchedulingNote';
import PinnedResourcePreview from './PinnedResource/PinnedResourcePreview';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Tooltip from 'src/components/Tooltip/Tooltip';
import VisibilityPill from 'src/components/VisibilityPill/VisibilityPill';
import {
  SaveSubscribersResult,
  Subscriber,
} from 'src/pages/ServiceRequest/Subscribers/CustomHooks/useServiceRequestSubscribers';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';
import { getDaysOpenPill } from 'src/utils/serviceRequestDaysOpen/getDaysOpenPill';
import { getLastUpdatedPill } from 'src/utils/serviceRequestLastUpdated/getLastUpdatedPill';
import './ServiceRequestDetailHeader.scss';

const SUBSCRIBED_TOOLTIP_CONTENT: string =
  "You're subscribed to updates on this service request but are not assigned to it.";

interface ServiceRequestDetailHeaderProps {
  serviceRequest: IServiceRequestDetail;
  location: any;
  onEditServiceRequest: () => void;
  onDeleteServiceRequest: () => void;
  onDownloadPdf: () => void;
  handleStatusChange: (statusFormData: any, serviceRequestId: any) => void;
  loadingSR?: boolean;
  homeOwnerDataVisibility: boolean;
  subscribers: Subscriber[];
  subscribersLoading: boolean;
  subscribersSaving: boolean;
  onSaveSubscribers: (userIds: string[]) => Promise<SaveSubscribersResult>;
  isCurrentUserSubscribed: boolean;
  pinRefreshKey?: number;
  onOpenPinnedNote?: () => void;
}

export const ServiceRequestDetailHeader = ({
  serviceRequest,
  location,
  onEditServiceRequest,
  onDeleteServiceRequest,
  onDownloadPdf,
  handleStatusChange,
  loadingSR,
  homeOwnerDataVisibility,
  subscribers,
  subscribersLoading,
  subscribersSaving,
  onSaveSubscribers,
  isCurrentUserSubscribed,
  pinRefreshKey,
  onOpenPinnedNote,
}: ServiceRequestDetailHeaderProps) => {
  const { branding } = useBranding();
  const user = useSelector((state: any) => selectAuthenticatedUser(state));

  const shouldHideHomeOwner: boolean =
    isRole([USER_ROLES.SERVICE_PROVIDER, USER_ROLES.VENDOR], user) &&
    !homeOwnerDataVisibility;

  const shouldHideReporter: boolean =
    serviceRequest?.suite?.homeOwner?.id === serviceRequest?.reporter?.id &&
    shouldHideHomeOwner;

  const canSeeSubscribers: boolean = !isRole(
    [USER_ROLES.HOME_OWNER, USER_ROLES.REALTOR, USER_ROLES.OCCUPANT],
    user
  );

  const canOpenContact: boolean = isRole(
    [USER_ROLES.PROPERTY_DEVELOPER, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN],
    user
  );

  const canAssign: boolean = isRole(
    [USER_ROLES.PROPERTY_DEVELOPER, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN],
    user
  );

  const canSeeAppointment: boolean = isRole(
    [
      USER_ROLES.PROPERTY_DEVELOPER,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.ADMIN,
      USER_ROLES.SERVICE_MANAGER,
    ],
    user
  );

  // Internal-only pills (internal/external visibility + days open) are hidden from external
  // parties (home owners, realtors, occupants); every internal/staff role sees them.
  const canSeeInternalStatusPills: boolean = !isRole(
    [USER_ROLES.HOME_OWNER, USER_ROLES.REALTOR, USER_ROLES.OCCUPANT],
    user
  );

  /* legacy/workaround: the API returns a placeholder home owner (empty GUID id,
   * blank name) for suites with no owner assigned — treat that as "no owner". */
  const homeOwner: any = serviceRequest?.suite?.homeOwner;

  const hasValidHomeOwner: boolean = Boolean(
    homeOwner &&
    homeOwner.id &&
    homeOwner.id !== '00000000-0000-0000-0000-000000000000' &&
    homeOwner.name &&
    homeOwner.name.trim()
  );

  const items: JSX.Element[] = [
    !shouldHideReporter && (
      <ServiceRequestReporterCard
        key="reporter"
        serviceRequest={serviceRequest}
        loading={loadingSR}
        canOpenContact={canOpenContact}
      />
    ),
    (loadingSR || (homeOwnerDataVisibility && hasValidHomeOwner)) && (
      <ServiceRequestHomeOwnerCard
        key="homeowner"
        serviceRequest={serviceRequest}
        loading={loadingSR}
        canOpenContact={canOpenContact}
      />
    ),
    <ServiceRequestAssignedToCard
      key="assigned"
      serviceRequest={serviceRequest}
      loading={loadingSR}
      handleStatusChange={handleStatusChange}
      canAssign={canAssign}
    />,
    canSeeSubscribers && (
      <SubscribersCard
        key="subscribers"
        serviceRequest={serviceRequest}
        user={user}
        subscribers={subscribers}
        loading={subscribersLoading || loadingSR}
        saving={subscribersSaving}
        onSaveSubscribers={onSaveSubscribers}
      />
    ),
  ].filter(Boolean);

  const ticketLabel: string =
    serviceRequest?.ticketNumber ?? String(serviceRequest?.ticketId ?? '');

  const daysOpen: number | null = serviceRequest?.daysOpen ?? null;

  const headlineNode: JSX.Element = loadingSR ? (
    <Skeleton variant="text" width="12rem" height="1.8rem" />
  ) : (
    <div className="sr-headline">
      <div className="headline-row">
        <span>{ticketLabel}</span>

        {isCurrentUserSubscribed && (
          <Tooltip content={SUBSCRIBED_TOOLTIP_CONTENT}>
            <CategoryPill
              text="Subscribed to email notifications"
              size="small"
              color="#000"
              textColor="#fff"
              iconGlyph="bell"
              iconWeight="solid"
            />
          </Tooltip>
        )}
      </div>

      {canSeeInternalStatusPills && (
        <div className="sr-heading-pill-row">
          <VisibilityPill
            isExternallyVisible={serviceRequest?.isExternallyVisible}
          />

          {daysOpen != null && daysOpen > 0 && (
            <>
              <span className="sr-heading-pill-divider" />
              <CategoryPill {...getDaysOpenPill(daysOpen)} size="small" />
            </>
          )}

          <span className="sr-heading-pill-divider" />
          <CategoryPill
            {...getLastUpdatedPill(serviceRequest?.updatedAt)}
            size="small"
          />
        </div>
      )}
    </div>
  );

  const statusValue: string | undefined = loadingSR
    ? undefined
    : serviceRequest.status?.name;

  return (
    <div className="sr-detail-header">
      <ServiceRequestHeading
        status={statusValue}
        breadcrumb={
          <ServiceRequestBreadcrumb
            user={user}
            location={location}
            branding={branding}
          />
        }
        toast={
          loadingSR ? null : (
            <>
              <ServiceRequestStatusToast
                serviceRequest={serviceRequest}
                handleStatusChange={handleStatusChange}
                user={user}
              />
              {canSeeAppointment && <ServiceRequestSchedulingNote compact />}
            </>
          )
        }
        headline={headlineNode}
        filedIn={
          <ServiceRequestLocationBreadcrumb
            serviceRequest={serviceRequest}
            user={user}
            loading={loadingSR}
          />
        }
        items={items}
        actions={
          <ServiceRequestActions
            serviceRequest={serviceRequest}
            onEditServiceRequest={onEditServiceRequest}
            onDeleteServiceRequest={onDeleteServiceRequest}
            onDownloadPdf={onDownloadPdf}
            user={user}
            loading={loadingSR}
          />
        }
        aside={
          canSeeAppointment ? (
            <div className="sr-appointment-aside">
              <ServiceRequestAppointmentCard
                serviceRequest={serviceRequest}
                loading={loadingSR}
                handleStatusChange={handleStatusChange}
              />
              {!loadingSR && (
                <div className="sr-appointment-pinned">
                  <PinnedResourcePreview
                    serviceRequestId={serviceRequest.id}
                    refreshKey={pinRefreshKey}
                    onOpenNote={onOpenPinnedNote}
                  />
                </div>
              )}
            </div>
          ) : null
        }
        style={undefined}
      />
    </div>
  );
};